import React, {Fragment} from 'react';
import __ from "lodash";
import { FormControl as MuiFormControl, FormGroup as MuiFormGroup, Grid as MuiGrid, Box as MuiBox, Tooltip, Table, TableHead, TableRow, TableCell, TableBody} from "@material-ui/core";
import { LabelledPaper, MaskedNumber } from '../../../controls';
import { getErrorClass, costTypes, totalFields } from '../includes/costingDialogFunctions';

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const LabelledBox = (props) => {
  return (
    <LabelledPaper 
      checkChildren={true}
      style={{display: "block", border: "solid 1px #b9b9b9" }}
      labelStyle={{background: "#f0f0f0"}}
      collapsible={false} 
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </LabelledPaper>
  )
}

const FormControl = (props) => {
  return (
    <MuiFormControl 
      checkChildren={true}
      style={{display: "block" }}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiFormControl>
  )
}

const Box = (props) => {
  return (
    <MuiBox 
      checkChildren={true}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiBox>
  )
}

const Grid = (props) => {
  return (
    <MuiGrid 
      checkChildren={true}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiGrid>
  )
}

const FormGroup = (props) => {
  return (
    <MuiFormGroup 
      checkChildren={true}
      style={{padding: "8px 8px 8px 8px"}}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiFormGroup>
  )
}

const LabelledValue = (props) => {
  const { classes, label, value, sx, displayProps, labelProps, valueProps, ...other } = props;
  return (
    <Box
      className={classes.labelledValue}
      sx={{
        display: 'inline-flex', 
        flexDirection: 'row',
        justifyContent: 'flex-start',
        ...sx,
      }}
      {...other}
    >
      <div key="label" className="label" {...labelProps}>{label}:</div>
      <div key="value" className="value" {...valueProps}><MaskedNumber displayType="text" value={value} {...displayProps}/></div>
    </Box>
  );
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const CostingTotals = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const {record, systemData: SystemData, classes, eventBus, rebuildRunning, costingErrors, readOnly} = props;

  const {costFilters} = record;
  const costingSectors = record?.legs||[];

  let contingencyExtras = readOnly?{displayType: "text"}:{};

  const handleContingencyBlockHoursRateChanged = (value) => {
    let contingencyAcmiCost = 0;
    const costType = __.find(costTypes, {code: 'ACMI'});
    for (let i = 0; i < costingSectors.length; i++) {
      const acmiCost = totalFields(costType, costingSectors[i], i, costFilters);
      let contingencyHoursAmount = Math.round((acmiCost * 100 * ((value||0) / 100)))/100;
      costingSectors[i]['contingencyHoursAmount'] = contingencyHoursAmount;
      contingencyAcmiCost += contingencyHoursAmount;
    }
    const changes = {contingencyBlockHoursRate: value, contingencyAcmiCost, legs: costingSectors};
    if (props.onChange) {
      props.onChange(changes);
    }
  }

  const handleContingencyFuelCostRateChanged = (value) => {
    let contingencyFuelCost = 0;
    const costType = __.find(costTypes, {code: 'Fuel'});
    for (let i = 0; i < costingSectors.length; i++) {
      const fuelCost = totalFields(costType, costingSectors[i], i, costFilters);
      let contingencyFuelAmount = Math.round((fuelCost * 100 * ((value||0) / 100)))/100;
      costingSectors[i]['contingencyFuelAmount'] = contingencyFuelAmount;
      contingencyFuelCost += contingencyFuelAmount;
    }
    const changes = {contingencyFuelCostRate: value, contingencyFuelCost, legs: costingSectors}
    if (props.onChange) {
      props.onChange(changes);
    }
  }

  if (record) {
    const costingHours = record.totalBlockHours + record.contingencyBlockHours;
    const totalCost = record.totalAcmiCost + record.totalFuelCost + record.groundCosts + record.totalOverflightCost + record.contingencyAcmiCost + record.contingencyFuelCost + record.truckDistributionCosts + record.otherFlightDistributionCosts + record.totalCommissions;
    return (
      <div key="CostingTotalsContainer" style={{width: "100%", textAlign:"left" }}>
        <Grid containerspacing={3}>
          <Grid item xs={12} row>
            <FormControl key="FC-addresses" >
              <FormGroup key="FG-addresses" style={{padding:0}}>
                <Box sx={{ flexDirection: 'row', width: "100%"}}>
                  <Box sx={{ display: 'inline-flex', flexDirection: 'column', width: "calc(50% - 8px)", pr:1 }}>
                    <LabelledBox label="" >
                      <div>
                        <SectionHeading>Operating Costs</SectionHeading>
                        <Table className={classes.operatingCosts}>
                          <TableBody>
                          <TableRow>
                              <TableCell style={{}}>ACMI</TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.totalAcmiCost} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Fuel</TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.totalFuelCost} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Ground Costs</TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.groundCosts} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Permit and Navigation Fees</TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.totalOverflightCost} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>
                                <div style={{display: "inline-block"}}>Contingency Hours</div>
                                <div style={{display: "inline-block", width: 50, textAlign: "right", float: "right", paddingRight: 20}}>
                                  <MaskedNumber style={{padding: 0}} value={record.contingencyBlockHoursRate||0} decimalScale={1} fixedDecimalScale={true} suffix="%" onChange={handleContingencyBlockHoursRateChanged} {...contingencyExtras}/>
                                </div>
                              </TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.contingencyAcmiCost} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>
                                <div style={{display: "inline-block"}}>Contingency Fuel</div>
                                <div style={{display: "inline-block", width: 50, textAlign: "right", float: "right", paddingRight: 20}}>
                                  <MaskedNumber style={{padding: 0}} value={record.contingencyFuelCostRate||0} decimalScale={1} fixedDecimalScale={true} suffix="%" onChange={handleContingencyFuelCostRateChanged} {...contingencyExtras}/>
                                </div>
                              </TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.contingencyFuelCost} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Costing Hours</TableCell>
                              <TableCell style={{}} width="100px" align="right"><Tooltip classes={classes} arrow placement="top" title={
                                <Fragment>
                                  <Grid container nowrap="true">
                                    <Grid item xs={8}>Block Hours:</Grid><Grid item xs={4} align="right"><MaskedNumber displayType="text" value={record.totalBlockHours} decimalScale={2} fixedDecimalScale={true} /></Grid>
                                  </Grid>
                                  <Grid container nowrap="true">
                                    <Grid item xs={8}>Contingency Hours:</Grid><Grid item xs={4} align="right"><MaskedNumber displayType="text" value={record.contingencyBlockHours} decimalScale={2} fixedDecimalScale={true} /></Grid>
                                  </Grid> 
                                </Fragment>
                              }><div><MaskedNumber displayType="text" value={costingHours} decimalScale={2} fixedDecimalScale={true} /></div></Tooltip></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Commission</TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.totalCommissions} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Freight Distribution - Truck</TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.truckDistributionCosts} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Freight Distribution - Other</TableCell>
                              <TableCell style={{}} width="100px" align="right"><MaskedNumber displayType="text" value={record.otherFlightDistributionCosts} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{backgroundColor: "#f0f0f0", fontWeight: 700}}>Total Operating Cost</TableCell>
                              <TableCell style={{backgroundColor: "#c9c9c9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={totalCost} prefix="$" decimalScale={2} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </LabelledBox>
                    <LabelledBox label="" style={{marginTop: 8}}>
                      <div>
                        <SectionHeading>Summary</SectionHeading>
                        <Table className={classes.summaryTotals}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Cost</TableCell>
                              <TableCell width="100px" align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell style={{fontWeight: 700}}>Total Revenue</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.totalRevenue} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{fontWeight: 700}}>Total Operating Cost</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={totalCost} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{fontWeight: 700}}>Contribution</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.contribution} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{fontWeight: 700}}>Overheads</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.overheads} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{fontWeight: 700}}>P&L</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.profit} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </LabelledBox>  
                  </Box>
                  <Box sx={{ display: 'inline-flex', flexDirection: 'column', width: "calc(50% - 8px)", pl:1 }}>
                    <LabelledBox label="" >
                      <div>
                        <SectionHeading>Per Hour Summary</SectionHeading>
                        <Table className={classes.summaryPerHour}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Cost</TableCell>
                              <TableCell width="100px" align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell style={{}}>Total Revenue</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.totalRevenue / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Fuel</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.totalFuelCost) / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Ground Costs</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.groundCosts) / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Permit and Navigation Fees</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.totalOverflightCost) / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Contingency/Fuel</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.contingencyFuelCost) / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>ACMI</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.totalAcmiCost + record.contingencyAcmiCost) / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{}}>Commission</TableCell>
                              <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.totalCommissions) / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{backgroundColor: "#f0f0f0", fontWeight: 700}}>Contribution</TableCell>
                              <TableCell style={{backgroundColor: "#c9c9c9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.contribution / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{backgroundColor: "#f0f0f0", fontWeight: 700}}>Overheads</TableCell>
                              <TableCell style={{backgroundColor: "#c9c9c9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.overheads / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{backgroundColor: "#f0f0f0", fontWeight: 700}}>P&L</TableCell>
                              <TableCell style={{backgroundColor: "#c9c9c9", fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={record.profit / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>      
                    </LabelledBox>
                    <LabelledBox label="" style={{marginTop: 8}}>
                      <div>
                        <SectionHeading>Gross Error Check</SectionHeading>
                        <Table className={classes.grossErrorCheck}>
                          <TableBody>
                            <TableRow className={getErrorClass(totalCost / costingHours, 'COST PER HOUR', costingErrors)}>
                              <TableCell style={{}}>Total Cost Per Hour</TableCell>
                              <TableCell className="isTotal" style={{fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={totalCost / costingHours} prefix="$" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow className={getErrorClass((record.totalFuelBurn) / costingHours, 'AVERAGE FUEL BURN', costingErrors)}>
                              <TableCell style={{}}>Average Fuel Burn (USG / Per Hour)</TableCell>
                              <TableCell className="isTotal" style={{fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.totalFuelBurn) / costingHours} suffix=" usg" decimalScale={0} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                            <TableRow className={getErrorClass((record.totalBlockHours / (costingSectors?.length)), 'CYCLE RATIO', costingErrors)}>
                              <TableCell style={{}}>Cycle Ratio</TableCell>
                              <TableCell className="isTotal" style={{fontWeight: 700}} width="100px" align="right"><MaskedNumber displayType="text" value={(record.totalBlockHours / (costingSectors?.length))} decimalScale={2} fixedDecimalScale={true} /></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </LabelledBox>
                  </Box>
                </Box>
              </FormGroup>
            </FormControl>
          </Grid>
        </Grid>
      </div>
    );
  }
  return (<></>);
}

export default CostingTotals;
